import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { ResponsiveValue } from 'styled-system';
import {
  color,
  display,
  height,
  margin,
  maxWidth,
  position,
  space,
  textAlign,
  themeGet,
} from 'styled-system';

import { BodyRegularBR1 } from 'common/components/Typography';
import { themeLeft, themeTop, translate3d } from 'common/theme/system-utilities';

const UnstyledStoererContainer = styled.span<any>`
  ${display};
  ${space};
  ${height};
  ${textAlign};
  ${position};
  ${margin};
  ${themeTop};
  ${themeLeft};
  ${color};
  ${translate3d};
  background-color: rgba(255, 255, 255, 0.85);
  border-top: 2px solid
    ${props =>
      props.borderColor ? props.borderColor : themeGet('colors.sys.primary.background.default')};
`;

const StoererContainer = (props: ComponentProps<typeof UnstyledStoererContainer>) => (
  <UnstyledStoererContainer
    display="inline-table"
    px="16px"
    margin={['auto', 0]}
    height="28px"
    textAlign="center"
    color="sys.neutral.border.active"
    {...props}
  />
);

const StyledTextBlock = styled(BodyRegularBR1)<any>`
  ${display};
  ${maxWidth};
  white-space: nowrap;
  text-overflow: clip;

  & > * {
    margin-top: 0;
    margin-bottom: 0;
  }

  & > :not(:first-of-type):not(br) {
    margin-top: ${themeGet('space.sp_16')}px;
  }
  & > :not(:last-child):not(br) {
    margin-bottom: ${themeGet('space.sp_16')}px;
  }
`;

const textBlockStyles: ComponentProps<typeof StyledTextBlock> = {
  display: 'inline-flex',
  maxWidth: '138px',
  overflow: 'hidden',
};

export interface StoererProps {
  /** Störer label */
  label: string;
  /** Störer border color */
  borderColor?: Nullable<string>;
  /** Specifies the vertical position of the Störer */
  themeTop?: ResponsiveValue<string | number>;
  /** Specifies the horizontal position of the Störer */
  themeLeft?: ResponsiveValue<string | number>;
  /** Specifies the position of the Störer */
  position?: ResponsiveValue<string | number>;
  /** Specifies the transform property of the Störer */
  translate3d?: ResponsiveValue<string | number>;
}

export const Stoerer = ({ label: html, ...containerProps }: StoererProps) => (
  <StoererContainer {...containerProps}>
    <StyledTextBlock
      {...textBlockStyles}
      data-testid="rich-text-block"
      dangerouslySetInnerHTML={{ __html: html }}
      borderColor={null}
      themeTop={0}
      themeLeft={['50%', 0]}
      position="absolute"
      translate3d={0}
    />
  </StoererContainer>
);
